import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, HttpClient, withXsrfConfiguration } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FormlyModule } from '@ngx-formly/core';
import { provideFormlyBuilder, ChunkedAttachmentUploader } from '@intalio/formly-builder';
import { provideFormlyBuilderBootstrap } from '@intalio/formly-builder/bootstrap';

import { routes } from './app.routes';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { AuthService } from './shared/services/auth.service';
import {
  AvsecTranslateAdapter,
  AvsecUserAdapter,
  AvsecAttachmentDownloaderAdapter,
} from './shared/adapters';

// Loader function
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(routes),
    provideHttpClient(
      withXsrfConfiguration({
        cookieName: 'X-CSRF-TOKEN',
        headerName: 'X-CSRF-TOKEN'
      }),
      withInterceptors([authInterceptor])
    ),

    // Auth Service
    AuthService,

    importProvidersFrom(
      ReactiveFormsModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }),
      // App-specific Formly types not provided by @intalio/formly-builder
      FormlyModule.forChild({
        types: [
          {
            name: 'datepicker',
            extends: 'input',
            defaultOptions: { templateOptions: { type: 'date' } },
          },
        ],
      })
    ),

    provideFormlyBuilder({
      translate: AvsecTranslateAdapter,
      user: AvsecUserAdapter,
      // file: omit -> library uses DefaultFileAdapter
      attachmentDownloader: AvsecAttachmentDownloaderAdapter,
      attachmentUploader: ChunkedAttachmentUploader,
      // actions, loader: omit -> library uses Noop adapters
      // Runtime settings come from public/assets/app-settings.js which is loaded
      // by index.html before bootstrap. Each `var` in that file becomes a window
      // global; we read them by name here so a deployment override (e.g. swapping
      // app-settings.js per VM/Docker env) takes effect without a rebuild.
      // app-settings.js is the only source of backend URLs. Services read the same
      // globals through the tokens in shared/config/app-settings.tokens.ts.
      appSettings: {
        apiRoot: (window as any).apiRoot,
        baseUrl: (window as any).baseUrl,
        production: (window as any).production,
        useHash: (window as any).useHash,
        userDetailsKey: (window as any).userDetailsKey,
        dateFormat: (window as any).dateFormat,
        fileExtensions: (window as any).fileExtensions,
      },
    }),

    provideFormlyBuilderBootstrap(),
  ]
};
